export enum AuthCallbackResolution {
    IdpInitiatedLogin = 'IDP_INITIATED_LOGIN',
    ParseHash = 'PARSE_HASH',
    SdkError = 'SDK_ERROR',
    Success = 'SUCCESS',
    Unresolved = 'UNRESOLVED',
    UrlError = 'URL_ERROR',
}

interface AuthCallbackState {
    hash: string;
    isAuthenticated: boolean;
    searchParams: URLSearchParams;
    error?: Error;
}

/**
 * Decides what the /auth_confirmed page should do, given the callback URL and the auth0-react state.
 * Only meaningful once auth0-react has finished loading.
 *
 * Every combination maps to a resolution. Anything the named branches don't cover is `Unresolved`,
 * which the caller must treat as a dead end to recover from rather than a state to wait in: the
 * inputs are not expected to change on their own, so waiting leaves the user on the loader forever.
 */
export function resolveAuthCallback({
    hash,
    isAuthenticated,
    searchParams,
    error,
}: AuthCallbackState): AuthCallbackResolution {
    if (hash) {
        // A passwordless redirect while auth0-react already holds a session — that session is usable as-is
        return isAuthenticated ? AuthCallbackResolution.Success : AuthCallbackResolution.ParseHash;
    }

    // No state param means the login was IdP-initiated
    if (searchParams.has('code') && searchParams.has('connection') && !searchParams.has('state')) {
        return AuthCallbackResolution.IdpInitiatedLogin;
    }

    if (searchParams.has('error')) {
        return AuthCallbackResolution.UrlError;
    }

    /**
     * auth0-react's handleRedirectCallback threw. It consumes the login transaction before exchanging
     * the code, so any second load of the callback URL (reload, PWA relaunch, tab restore) or an
     * interrupted exchange ends here with `missing_transaction` / `Invalid state`. The ?code&state
     * params are left in the URL, so reloading fails identically.
     */
    if (error) {
        return AuthCallbackResolution.SdkError;
    }

    // Wait for auth0-react to parse and strip the code
    if (isAuthenticated && !searchParams.has('code')) {
        return AuthCallbackResolution.Success;
    }

    return AuthCallbackResolution.Unresolved;
}

/**
 * auth0-spa-js errors carry an OAuth-style code (e.g. `missing_transaction`, `invalid_grant`)
 * alongside the human-readable message.
 */
export function getAuth0ErrorCode(error?: Error): string | undefined {
    if (error && 'error' in error && typeof error.error === 'string') {
        return error.error;
    }
    return undefined;
}

/**
 * Auth0 adaptive MFA rejects a sign-in it rates low-confidence (typically a new device) when it
 * has no second factor to challenge the user with. SMS passwordless can't offer one, so these users
 * have to go through ULP instead, which can prompt for email + password and the 2FA code.
 *
 * `access_denied` alone is too broad — Auth0 also uses it for declined consent and expired login
 * tickets — so the description has to be matched too.
 */
export function isLowConfidenceLoginError(code?: string, description?: string): boolean {
    return code === 'access_denied' && /low-confidence/i.test(description ?? '');
}

export enum TokenErrorResolution {
    Ignore = 'IGNORE',
    LoginRequired = 'LOGIN_REQUIRED',
    Logout = 'LOGOUT',
    Retry = 'RETRY',
    VerificationRequired = 'VERIFICATION_REQUIRED',
}

// Auth0 needs the user to log in interactively, so a silent retry can never succeed
const InteractiveLoginErrorCodes = new Set([
    'consent_required',
    'interaction_required',
    'login_required',
]);

interface TokenErrorState {
    hasSession: boolean;
    isRetry: boolean;
    error?: Error;
}

/**
 * Decides how AuthTokenCheck should respond to a failed getAccessTokenSilently call.
 *
 * `hasSession` is whether there is anything to log out of: an auth0-react session or a bearer
 * token. Without one, a failure only confirms the user isn't logged in. A full logout there
 * redirects through Auth0 back to the login page, whose own token check then fails and logs out
 * again, in a loop that reloads the login page every couple of minutes.
 */
export function resolveTokenError({
    hasSession,
    isRetry,
    error,
}: TokenErrorState): TokenErrorResolution {
    const code = getAuth0ErrorCode(error);

    // Retrying can't help, and a full logout would replace the explanation on the login page
    if (isLowConfidenceLoginError(code, error?.message)) {
        return TokenErrorResolution.VerificationRequired;
    }

    if (
        (code && InteractiveLoginErrorCodes.has(code)) ||
        /login required/i.test(error?.message ?? '')
    ) {
        return TokenErrorResolution.LoginRequired;
    }

    if (!hasSession) {
        return TokenErrorResolution.Ignore;
    }

    /**
     * Retry once to handle the "Error: Invalid State" that can occur when the two Auth0 libraries
     * are used together.
     * See: https://community.auth0.com/t/invalid-state-on-reload-auth0-callback-url-using-auth0-spa-js-and-angular-8/36469/10
     */
    return isRetry ? TokenErrorResolution.Logout : TokenErrorResolution.Retry;
}

/**
 * Describes the callback URL for logging without leaking the auth code or tokens it carries.
 */
export function describeAuthCallbackUrl(hash: string, searchParams: URLSearchParams) {
    return {
        hasHash: !!hash,
        searchParamKeys: [...new Set(searchParams.keys())].sort(),
    };
}
